<template>
  <div class="desktop-slider">
    <div class="slider-selectors">
      <div class="thumbs" ref="desktopSliderWrapper" @scroll="onScroll">
        <div
          class="thumb-photo"
          v-for="(photo, index) in photos"
          :key="index"
          :class="{ active: activeIndex === index }"
          @click="setActiveIndex(index)"
          data-test-id="list__vertical-photo-carousel"
        >
          <!-- TODO: Replace with ImgWithFallback from @/shared/ui when available -->
          <img width="75" height="100" class="image" :src="photo.thumbnail" :alt="alt" />
        </div>
        <div class="thumb-indicator" :style="styleThumbIndicator"></div>
      </div>
      <transition name="fade">
        <div class="navigation top" v-if="isShownTopNavigation">
          <div class="button" @click="moveList(false)">
            <IconChevronRightBold :size="16" />
          </div>
        </div>
      </transition>
      <transition name="fade">
        <div class="navigation bottom" v-if="isShownBottomNavigation">
          <div class="button" @click="moveList(true)">
            <IconChevronRightBold :size="16" />
          </div>
        </div>
      </transition>
    </div>

    <div class="main-photo" data-test-id="list__carousel-photo">
      <div class="main-photo__content">
        <!-- TODO: Consider replacing with keen-slider component when available for better performance and consistency -->
        <transition name="fade" mode="out-in">
          <ProductCircularView
            v-if="isShownCircular"
            :circularPhotos="props.circularPhotos || []"
            :isCardViewOpenedProp="true"
            :fullscreenEnable="false"
            @close="close"
            @setZIndex="() => {}"
          />

          <ProductVideo
            v-else-if="isShownVideo"
            :videoURL="props.video || ''"
            :isMutedProp="true"
            :isPlayingProp="true"
            :isCardViewOpenedProp="true"
            :fullscreenEnable="false"
            @close="close"
            @setZIndex="() => {}"
          />

          <!-- TODO: Replace with ImgWithFallback from @/shared/ui when available -->
          <img
            @click="onMainImageClick"
            class="main-photo__content__image"
            v-else
            :src="activePhoto?.high"
            :alt="alt"
            :key="'main-photo' + activeIndex"
          />
        </transition>

        <div class="actions-buttons">
          <actions-buttons
            v-if="!isShownCircular && !isShownVideo"
            :hasCircular="hasCircular"
            :hasVideo="hasVideo"
            @showCircular="showCircular"
            @showVideo="showVideo"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// TODO: Replace with SweetScroll from 'sweet-scroll' when available
// ProductVideo component implemented and integrated
// TODO: Replace with ProductCircularView from @/shared/components/ui/product-circular-view/index.vue when available
// TODO: Replace with ImgWithFallback from @/shared/ui when available
// TODO: Replace with IconUi from @/shared/legacy-kit when available

import { ref, computed, watch, onMounted, nextTick } from 'vue';
import type { IPhoto, ISkuPhotoItemCharacteristic } from '../../models/slider';
import { useProductPage } from '../../business/use-product-page';
import { useProductModals } from '../../business/useProductModals';
import ActionsButtons from '../common/actions-buttons.vue';
import ProductVideo from '../common/ProductVideo.vue';
import ProductCircularView from '../common/ProductCircularView.vue';
import { IconChevronRightBold } from '@/features/ui-kit/icons';

const NAVIGATION_THRESHOLD = 50;
const THUMB_HEIGHT = 101;
const THUMB_GAP = 12;
const THUMB_INDICATOR_THRESHOLD = 0.5;

const duration = 250;
const easing = 'easeInOutSine';

interface Props {
  photos: IPhoto[];
  circularPhotos: string[];
  video?: string | null;
  alt: string;
}

const props = withDefaults(defineProps<Props>(), {
  photos: () => [],
  circularPhotos: () => [],
  video: null,
  alt: '',
});

const activeIndex = ref(0);
const scrollTop = ref(0);
const isShownBottomNavigation = ref(false);
const isShownCircular = ref(false);
const isShownVideo = ref(false);
const isShownTopNavigation = ref(false);
const listElement = ref<HTMLElement | null>(null);
const styleThumbIndicator = ref({
  transform: `translateY(${THUMB_INDICATOR_THRESHOLD}px)`,
});
const defaultUrl = ref<IPhoto | null>(null);

const desktopSliderWrapper = ref<HTMLElement>();

// Get product page instance
const { state } = useProductPage();
const { openGalleryPopup } = useProductModals();

const activePhoto = computed(() => {
  return props.photos[activeIndex.value] || defaultUrl.value;
});

const hasCircular = computed((): boolean => {
  return props.circularPhotos && props.circularPhotos?.length > 0;
});

const hasVideo = computed((): boolean => {
  return Boolean(props.video && props.video.length > 0);
});

const currentSku = computed(() => {
  return state.currentSku;
});

watch(currentSku, () => {
  const photoCharacteristic: ISkuPhotoItemCharacteristic | undefined =
    currentSku.value?.characteristics?.find(
      (characteristic: any) => characteristic.type === 'image'
    ) as ISkuPhotoItemCharacteristic;

  const index = photoCharacteristic
    ? props.photos.findIndex((photo) => photo.photoKey === photoCharacteristic.value.photoKey)
    : 0;

  setActiveIndex(index);
});

onMounted(async () => {
  await nextTick();
  if (desktopSliderWrapper.value) {
    listElement.value = desktopSliderWrapper.value;
    updateNavigation(listElement.value);
    defaultUrl.value = props.photos[0] || null;
  }
});

const setActiveIndex = (index: number) => {
  if (props.photos[activeIndex.value]) {
    defaultUrl.value = props.photos[activeIndex.value];
  }
  activeIndex.value = index;
  close();
};

const updateNavigation = (el: HTMLElement) => {
  isShownBottomNavigation.value =
    el.scrollTop + el.offsetHeight + NAVIGATION_THRESHOLD < el.scrollHeight;
  isShownTopNavigation.value = el.scrollTop >= NAVIGATION_THRESHOLD;
};

const onScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  updateNavigation(target);
};

const showCircular = () => {
  isShownCircular.value = true;
};

const showVideo = () => {
  isShownVideo.value = true;
};

const close = () => {
  isShownCircular.value = false;
  isShownVideo.value = false;
};

const onMainImageClick = () => {
  openGalleryPopup(props.photos, activeIndex.value, props.alt, (activeIndex: number) => {
    setActiveIndex(activeIndex);
  });
};

/**
 * Function is used for navigation by arrows
 * There's some logic for clearly scroll to the edge of the thumb photo
 */
const moveList = (isBottom = false): void => {
  if (!listElement.value) return;

  const parent = listElement.value;
  let newScrollTop = isBottom
    ? parent.scrollTop + THUMB_HEIGHT + THUMB_GAP
    : parent.scrollTop - THUMB_HEIGHT - THUMB_GAP;

  if (!isBottom && newScrollTop <= THUMB_HEIGHT + THUMB_GAP) {
    newScrollTop = 0;
  }

  // TODO: Replace with SweetScroll.create({ duration, easing }, parent).toTop(newScrollTop) when available
  parent.scrollTo({ top: newScrollTop, behavior: 'smooth' });
};

const scrollToThumb = (index: number): void => {
  if (!listElement.value) return;

  let newScrollTop = index * THUMB_HEIGHT + THUMB_INDICATOR_THRESHOLD;

  // TODO: Replace with SweetScroll.create({ duration, easing }, this.listElement).toTop(newScrollTop) when available
  listElement.value.scrollTo({ top: newScrollTop, behavior: 'smooth' });
};

watch(activeIndex, (val: number): void => {
  styleThumbIndicator.value = {
    transform: `translateY(${val * (THUMB_HEIGHT + THUMB_GAP) + THUMB_INDICATOR_THRESHOLD}px)`,
  };
  scrollToThumb(val);
});
</script>

<style lang="stylus">
.fade-enter-active
.fade-leave-active
  transition opacity .25s cubic-bezier(0.65, 0, 0.35, 1)

.fade-enter
.fade-leave-to
  opacity 0

.desktop-slider
  --slider-gap 11px
  --thumbs-width 77px
  --photo-ratio calc(527/394)
  width 100%
  display flex
  justify-content: flex-end;
  align-items flex-start
  position relative

  .slider-selectors
    width var(--thumbs-width)
    overflow-y hidden
    position absolute
    top 0
    bottom 0
    left 0
    .navigation
      position absolute
      bottom 0
      left 0
      right 0
      display flex
      justify-content center
      height 48px
      z-index 1
      background linear-gradient(0deg, #FFFFFF 2.23%, #FFFFFF 47.55%, rgba(255, 255, 255, 0) 100%)
      .button
        height 40px
        width 40px
        cursor pointer
        border-radius 20px
        background White(A60)
        box-shadow 0px 0px 4px rgba(0, 0, 0, 0.04)
        backdrop-filter blur(4px)
        -webkit-backdrop-filter blur(4px)
        display flex
        justify-content center
        align-items center
        font-size 20px
        transition all .25s ease-in-out
        &:hover
          background White(A70)
        &:active
          background White(A80)
      &.top
        bottom auto
        top 0
        & i
          transform rotate(-90deg)
          margin-bottom 4px
          color var(--text-secondary)
      &.bottom
        & i
          transform rotate(90deg)
          margin-top 6px
          color var(--text-secondary)
    .thumbs
      width 100%
      height 100%
      position relative
      display grid
      grid-template-columns 76px
      grid-auto-rows 101px
      grid-row-gap 12px
      grid-auto-flow row

      overflow-y auto
      -ms-overflow-style none
      scrollbar-width none
      scrollbar-height 0
      -webkit-touch-callout none /* iOS Safari */
      -webkit-user-select none /* Safari */
      -moz-user-select none /* Old versions of Firefox */
      -ms-user-select none /* Internet Explorer/Edge */
      user-select none
      &::-webkit-scrollbar
        display none
      .thumb-indicator
        position absolute
        top 0
        left 0
        right 0
        height 98px
        border-radius 12px
        outline 1px solid #FFFFFF
        outline-offset -3px
        z-index 1
        transition transform .3s ease-out
        will-change transform
        border solid 2px var(--border-focused)
      .thumb-photo
        height 101px
        width 76px
        cursor pointer
        display flex
        align-items center
        justify-content center
        border-radius 12px
        overflow hidden
        background-color #EFEFEF
        .image
          height auto
          width 100%
          transition all .25s ease-in-out
          object-fit cover

  .main-photo
    width calc(100% - 88px)
    min-width calc(100% - 88px)
    overflow hidden
    user-select none
    display flex
    align-items center
    justify-content center
    position relative

    &__content
      aspect-ratio 3/4
      border-radius 20px
      background Gray(A10)
      max-width 396px
      height auto
      width 100%
      position relative
      cursor pointer
      overflow hidden

      &__image
        object-fit cover
        position absolute
        top 0
        left 0
        height 100%
        width 100%

.actions-buttons
  +Media(Laptop, Desktop, DesktopMax)
    position absolute
    bottom 10px
    left 10px
</style>
